import { RefObject } from 'react';
import { IconCheck, IconX } from '@rossum/ui/icons/tabler';
import {
  IconButton,
  InputAdornment,
  SvgIcon,
  TextField,
  Typography,
  TypographyProps,
} from '@rossum/ui/material';

type InlineRenameFieldProps = {
  name: string;
  isRenaming: boolean;
  editedName: string;
  setEditedName: (name: string) => void;
  handleKeyDown: (event: React.KeyboardEvent) => void;
  handleSaveRename: () => void;
  handleCancelRename: () => void;
  inputRef: RefObject<HTMLInputElement | null>;
  variant?: TypographyProps['variant'];
};

const VARIANT_HEIGHT: Partial<
  Record<NonNullable<TypographyProps['variant']>, number>
> = {
  subtitle2: 30,
};

export const InlineRenameField = ({
  name,
  isRenaming,
  editedName,
  setEditedName,
  handleKeyDown,
  handleSaveRename,
  handleCancelRename,
  inputRef,
  variant = 'body1',
}: InlineRenameFieldProps) => {
  const height = variant ? VARIANT_HEIGHT[variant] : undefined;

  if (!isRenaming) {
    return (
      <Typography
        variant={variant}
        noWrap
        sx={height ? { height, lineHeight: `${height}px` } : undefined}
      >
        {name}
      </Typography>
    );
  }

  return (
    <TextField
      inputRef={inputRef}
      value={editedName}
      onChange={e => setEditedName(e.target.value)}
      onKeyDown={handleKeyDown}
      variant="standard"
      fullWidth
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleSaveRename}>
                <SvgIcon fontSize="medium">
                  <IconCheck />
                </SvgIcon>
              </IconButton>
              <IconButton size="small" onClick={handleCancelRename}>
                <SvgIcon fontSize="medium">
                  <IconX />
                </SvgIcon>
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
      sx={t => ({
        '& .MuiInputBase-root': {
          ...(variant && variant !== 'inherit' ? t.typography[variant] : {}),
          color: t.palette.text.primary,
          ...(height ? { height } : {}),
        },
        '& .MuiInputBase-input': {
          padding: 0,
        },
      })}
    />
  );
};
